/**
 * Smart Input Component
 * Text area that accepts natural language, structured text, or JSON
 * and parses it into structured travel data.
 *
 * Flow: Regex first (free/fast) → user can delete bad fields,
 * AI re-extract individual fields, or re-parse everything with AI.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSmartParser } from '../../hooks/useSmartParser';
import { getFieldLabel } from '../../utils/fieldSchemas';

const SmartInput = ({
  documentType = 'flight',
  value = '',
  onChange,
  onParse,
  onApply,
  disabled = false,
  placeholder,
  className = ''
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef(null);
  const {
    parse,
    parseWithAI,
    parseFieldWithAI,
    removeField,
    sendFeedback,
    parseResult,
    isProcessing,
    fieldProcessing,
    error,
    hasData,
    extractedFields,
    getConfidenceLevel,
    clearResult
  } = useSmartParser(documentType);

  // Sync with external value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Handle input change
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange?.(newValue);

    if (showPreview) {
      setShowPreview(false);
      clearResult();
    }
  }, [onChange, showPreview, clearResult]);

  // Handle regex parse
  const handleParse = useCallback(() => {
    if (!localValue.trim()) return;
    const result = parse(localValue);
    setShowPreview(true);
    onParse?.(result);
  }, [localValue, parse, onParse]);

  // Handle AI-first parse
  const handleParseAI = useCallback(async () => {
    if (!localValue.trim()) return;
    setShowPreview(true);
    const result = await parseWithAI(localValue);
    onParse?.(result);
  }, [localValue, parseWithAI, onParse]);

  // Handle full AI re-parse (from preview)
  const handleReparseWithAI = useCallback(async () => {
    const result = await parseWithAI();
    onParse?.(result);
  }, [parseWithAI, onParse]);

  // Handle per-field AI re-extract
  const handleFieldAI = useCallback(async (fieldName) => {
    await parseFieldWithAI(fieldName);
  }, [parseFieldWithAI]);

  // Handle field deletion
  const handleFieldDelete = useCallback((fieldName) => {
    removeField(fieldName);
  }, [removeField]);

  // Handle apply action
  const handleApply = useCallback(() => {
    if (parseResult?.data) {
      // Send feedback if corrections were made (fire-and-forget)
      sendFeedback();

      onApply?.(parseResult.data);
      setLocalValue('');
      setShowPreview(false);
      clearResult();
      onChange?.('');
    }
  }, [parseResult, onApply, onChange, clearResult, sendFeedback]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if (showPreview && hasData) {
        handleApply();
      } else {
        handleParse();
      }
    }
    if (e.key === 'Escape' && showPreview) {
      setShowPreview(false);
      clearResult();
    }
  }, [showPreview, hasData, handleApply, handleParse, clearResult]);

  // Focus textarea
  const focus = useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  // Get placeholder text
  const getPlaceholder = () => {
    if (placeholder) return placeholder;
    switch (documentType) {
      case 'flight':
      case 'commercial_flight':
        return 'Paste or type flight details...\n\nExamples:\n• JFK → LAX Dec 15 10:30 AA123 John Smith\n• Multi-leg: L1 JFK-LAX Dec 15; L2 LAX-SFO Dec 17';
      case 'hotel':
        return 'Paste or type hotel details...\n\nExamples:\n• Marriott NYC Dec 15-18, John Smith, conf ABC123';
      case 'car_rental':
        return 'Paste or type car rental details...\n\nExamples:\n• Hertz LAX Dec 15-18 midsize, John Smith';
      default:
        return 'Paste or type travel details...';
    }
  };

  // Confidence badge color
  const getConfidenceBadgeColor = (confidence) => {
    const level = getConfidenceLevel(confidence);
    switch (level) {
      case 'high': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div className={`flex flex-col ${className}`}>
      {/* Input area */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={localValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={getPlaceholder()}
          rows={4}
          className={`w-full px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
            disabled ? 'bg-gray-50 text-gray-500' : 'bg-white'
          } ${showPreview ? 'border-blue-300' : 'border-gray-300'}`}
        />
        <div className="absolute bottom-2 right-2 text-xs text-gray-400">
          {localValue.length > 0 && `${localValue.length} chars`}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-between mt-2">
        <div className="text-xs text-gray-500">
          {showPreview ? (
            <span className="text-blue-600">
              Press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">Ctrl+Enter</kbd> to apply
            </span>
          ) : (
            <span>
              Press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-xs">Ctrl+Enter</kbd> to parse
            </span>
          )}
        </div>

        <div className="flex gap-2">
          {showPreview && (
            <button
              type="button"
              onClick={() => { setShowPreview(false); clearResult(); }}
              className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
            >
              Cancel
            </button>
          )}
          {showPreview && hasData ? (
            <button
              type="button"
              onClick={handleApply}
              disabled={disabled || isProcessing}
              className="px-4 py-1.5 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Apply
            </button>
          ) : (
            <div className="flex gap-1.5">
              {/* Regex parse button */}
              <button
                type="button"
                onClick={handleParse}
                disabled={disabled || isProcessing || !localValue.trim()}
                className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                title="Parse with regex (fast, free)"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Parse
              </button>

              {/* AI parse button */}
              <button
                type="button"
                onClick={handleParseAI}
                disabled={disabled || isProcessing || !localValue.trim()}
                className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                title="Parse with AI (slower, more accurate)"
              >
                {isProcessing ? (
                  <>
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    AI...
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.141-.847a1 1 0 111.19 1.61l-.34.252 1.869 7.476a1 1 0 01-.398 1.053l-.007.005A3.96 3.96 0 0115 17a3.96 3.96 0 01-3.41-1.546l-.006-.005a1 1 0 01-.398-1.053l1.869-7.476-.34-.252a1 1 0 01.263-1.735l.007-.003L11 4.323V3a1 1 0 011-1z" />
                    </svg>
                    AI Parse
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Preview of extracted fields */}
      {showPreview && (
        <div className="mt-3 border border-blue-200 rounded-lg bg-blue-50 overflow-hidden">
          {/* Preview header */}
          <div className="px-3 py-2 bg-blue-100 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-blue-800">
                Extracted Fields
              </span>
              <div className="flex items-center gap-2">
                {/* Re-parse with AI button */}
                {parseResult?.format !== 'ai' && (
                  <button
                    type="button"
                    onClick={handleReparseWithAI}
                    disabled={isProcessing}
                    className="px-2 py-1 text-xs text-purple-700 hover:text-purple-900 bg-purple-100 hover:bg-purple-200 rounded flex items-center gap-1 disabled:opacity-50"
                    title="Discard regex results and re-parse everything with AI"
                  >
                    {isProcessing ? (
                      <>
                        <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Parsing...
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.141-.847a1 1 0 111.19 1.61l-.34.252 1.869 7.476a1 1 0 01-.398 1.053l-.007.005A3.96 3.96 0 0115 17a3.96 3.96 0 01-3.41-1.546l-.006-.005a1 1 0 01-.398-1.053l1.869-7.476-.34-.252a1 1 0 01.263-1.735l.007-.003L11 4.323V3a1 1 0 011-1z" />
                        </svg>
                        Re-parse with AI
                      </>
                    )}
                  </button>
                )}
                {parseResult?.format && (
                  <span className="text-xs text-blue-600">
                    Format: {parseResult.format}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Field list */}
          <div className="p-3">
            {hasData ? (
              <div className="space-y-1">
                {extractedFields.filter(({ field }) => field !== 'legs').map(({ field, value, confidence }) => (
                  <div key={field} className="flex items-center gap-2 text-sm group py-0.5">
                    {/* Confidence badge */}
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${getConfidenceBadgeColor(confidence)}`}>
                      {confidence >= 0.9 ? '✓' : confidence >= 0.7 ? '~' : '?'}
                    </span>

                    {/* Field label */}
                    <span className="text-gray-600 min-w-[110px] flex-shrink-0 text-xs">
                      {getFieldLabel(documentType, field)}:
                    </span>

                    {/* Value */}
                    <span className="text-gray-900 font-medium flex-1 break-all text-xs">
                      {fieldProcessing === field ? (
                        <span className="text-purple-600 flex items-center gap-1">
                          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          AI extracting...
                        </span>
                      ) : (
                        typeof value === 'object' ? JSON.stringify(value) : String(value)
                      )}
                    </span>

                    {/* Action buttons */}
                    <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      {/* AI re-extract */}
                      <button
                        type="button"
                        onClick={() => handleFieldAI(field)}
                        disabled={isProcessing || fieldProcessing !== null}
                        className="p-1 text-purple-500 hover:text-purple-700 hover:bg-purple-100 rounded disabled:opacity-30"
                        title={`Re-extract "${getFieldLabel(documentType, field)}" with AI`}
                      >
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.141-.847a1 1 0 111.19 1.61l-.34.252 1.869 7.476a1 1 0 01-.398 1.053l-.007.005A3.96 3.96 0 0115 17a3.96 3.96 0 01-3.41-1.546l-.006-.005a1 1 0 01-.398-1.053l1.869-7.476-.34-.252a1 1 0 01.263-1.735l.007-.003L11 4.323V3a1 1 0 011-1z" />
                        </svg>
                      </button>
                      {/* Delete */}
                      <button
                        type="button"
                        onClick={() => handleFieldDelete(field)}
                        disabled={isProcessing || fieldProcessing !== null}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-30"
                        title={`Remove "${getFieldLabel(documentType, field)}" from results`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}

                {/* Legs preview (for multi-leg flights) */}
                {parseResult?.data?.legs && Array.isArray(parseResult.data.legs) && parseResult.data.legs.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-blue-100">
                    <div className="text-xs font-medium text-gray-600 mb-1">
                      {parseResult.data.legs.length} Legs Detected:
                    </div>
                    {parseResult.data.legs.map((leg, idx) => {
                      const from = leg.departure_airport || leg.departure_airport_icao || '?';
                      const to = leg.arrival_airport || leg.arrival_airport_icao || '?';
                      const date = leg.departure_date || '';
                      const time = leg.departure_time || '';
                      const flight = leg.flight_number ? `${leg.airline_code || ''}${leg.flight_number}` : '';
                      return (
                        <div key={idx} className="flex items-center gap-2 py-0.5 text-xs">
                          <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                            {idx + 1}
                          </span>
                          <span className="font-medium text-gray-900">{from} → {to}</span>
                          {date && <span className="text-gray-500">{date}</span>}
                          {time && <span className="text-gray-500">{time}</span>}
                          {flight && <span className="text-gray-400">{flight}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-blue-700 text-center py-2">
                No data could be extracted. Try a different format or use AI Parse.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error display */}
      {showPreview && error && (
        <div className="mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          {error}
        </div>
      )}

      {/* Format hints */}
      {!showPreview && localValue.length === 0 && (
        <div className="mt-2 text-xs text-gray-400">
          <details className="cursor-pointer">
            <summary className="hover:text-gray-600">Supported input formats</summary>
            <div className="mt-2 pl-4 space-y-1 text-gray-500">
              <p><strong>Natural language:</strong> "John Smith flying AA 123 from JFK to LAX on Dec 15"</p>
              <p><strong>Arrow notation:</strong> JFK → LAX, Dec 15, 10:30</p>
              <p><strong>Key-value:</strong> from: JFK, to: LAX, date: Dec 15</p>
              <p><strong>JSON:</strong> {"{"}"departure_airport": "JFK", ...{"}"}</p>
              <p><strong>Paste:</strong> Copy text directly from confirmation emails</p>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

// Export focus method for external use
SmartInput.focus = (ref) => {
  ref?.current?.focus?.();
};

export default SmartInput;
